import React, { useState, useEffect, useCallback, useRef } from 'react';
import Timer from './Timer';
import ScoreDisplay from './ScoreDisplay';
import ProgressBar from './ProgressBar';
import LicensePlateImage from './LicensePlateImage';
import { useLanguage } from '../contexts/LanguageContext';
import { countriesData } from '../data/countries';
import { getRandomImageNumber } from '../utils/gameUtils';

const PlateGame = ({ difficulty, gameData, onUpdateGameData, onGameOver, onBack }) => {
  const { t, getCountries } = useLanguage();
  const [currentCountry, setCurrentCountry] = useState(null);
  const [currentImageNumber, setCurrentImageNumber] = useState(null);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [guessValue, setGuessValue] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const startTimeRef = useRef(null);
  const countries = getCountries();

  // FIXED: Easy mode filtering - less restrictive
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setGuessValue(value);
    
    if (value.length === 0 || hasGuessed) {
      setShowAutocomplete(false);
      setFilteredCountries([]);
      setSelectedIndex(-1);
      return;
    }

    let filtered;
    const valueLower = value.toLowerCase();
    
    if (difficulty === 'easy') {
      // Easy mode: Show countries by name, but exclude ONLY exact code matches
      filtered = countries.filter(country => {
        const nameMatches = country.name.toLowerCase().includes(valueLower);
        const isExactCodeMatch = country.code.toLowerCase() === valueLower;
        
        // Show name matches but exclude only if it's an EXACT code match
        // This allows "lat" to show "Latvia" but blocks "cz" from showing "Czechia"
        return nameMatches && !isExactCodeMatch;
      });
    } else {
      // Hard mode: Show both name and code matches with proper sorting
      filtered = countries.filter(country => 
        country.name.toLowerCase().includes(valueLower) ||
        country.code.toLowerCase().includes(valueLower)
      );
      
      // Sort by priority - exact code match first, then code starts with, then name matches
      filtered.sort((a, b) => {
        const aCodeExact = a.code.toLowerCase() === valueLower;
        const bCodeExact = b.code.toLowerCase() === valueLower;
        
        // 1. Exact code matches first
        if (aCodeExact && !bCodeExact) return -1;
        if (!aCodeExact && bCodeExact) return 1;
        
        const aCodeStarts = a.code.toLowerCase().startsWith(valueLower);
        const bCodeStarts = b.code.toLowerCase().startsWith(valueLower);
        
        // 2. Code starts with matches second
        if (aCodeStarts && !bCodeStarts) return -1;
        if (!aCodeStarts && bCodeStarts) return 1;
        
        const aNameStarts = a.name.toLowerCase().startsWith(valueLower);
        const bNameStarts = b.name.toLowerCase().startsWith(valueLower);
        
        // 3. Name starts with matches third
        if (aNameStarts && !bNameStarts) return -1;
        if (!aNameStarts && bNameStarts) return 1;
        
        // 4. Everything else (name contains) in alphabetical order
        return a.name.localeCompare(b.name);
      });
    }

    filtered = filtered.slice(0, 5);
    setFilteredCountries(filtered);
    setShowAutocomplete(filtered.length > 0);
    setSelectedIndex(-1);
  }, [difficulty, hasGuessed, countries]);

  const startNewRound = useCallback(() => {
    if (gameData.remainingCountries.length === 0) {
      const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
      onGameOver({ elapsedTime });
      return;
    }

    const randomIndex = Math.floor(Math.random() * gameData.remainingCountries.length);
    const country = gameData.remainingCountries[randomIndex];
    const newRemaining = gameData.remainingCountries.filter((_, index) => index !== randomIndex);
    
    setCurrentCountry(country);
    setCurrentImageNumber(getRandomImageNumber());
    setHasGuessed(false);
    setFeedback(null);
    setGuessValue('');
    setShowAutocomplete(false);
    setFilteredCountries([]);
    setSelectedIndex(-1);
    
    onUpdateGameData({ remainingCountries: newRemaining });
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, [gameData.remainingCountries, onUpdateGameData, onGameOver]);

  useEffect(() => {
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
    if (!currentCountry && gameData.remainingCountries.length > 0) {
      startNewRound();
    }
  }, [currentCountry, gameData.remainingCountries, startNewRound]);

  const submitGuess = useCallback((valueToSubmit = null) => {
    const guessToCheck = valueToSubmit !== null ? valueToSubmit : guessValue;
    
    if (hasGuessed) return;
    if (!guessToCheck || (typeof guessToCheck === 'string' && guessToCheck.trim() === '')) return;

    const guess = guessToCheck.trim().toLowerCase();
    let isCorrect;
    
    // Get country names in both languages for comparison
    const englishCountries = countriesData.en;
    const frenchCountries = countriesData.fr;
    
    const englishCountry = englishCountries.find(c => c.code === currentCountry.code);
    const frenchCountry = frenchCountries.find(c => c.code === currentCountry.code);
    
    if (difficulty === 'easy') {
      // Accept answers in both English and French
      isCorrect = guess === englishCountry.name.toLowerCase() || 
                 guess === frenchCountry.name.toLowerCase();
    } else {
      // Accept country names in both languages OR the code
      isCorrect = guess === englishCountry.name.toLowerCase() || 
                 guess === frenchCountry.name.toLowerCase() ||
                 guess === currentCountry.code.toLowerCase();
    }

    const newScore = isCorrect ? gameData.score + 1 : gameData.score;
    const newTotalAttempts = gameData.totalAttempts + 1;

    setHasGuessed(true);
    setFeedback({
      isCorrect,
      message: isCorrect ? t('correct') : t('incorrect')
    });
    setShowAutocomplete(false);
    setFilteredCountries([]);
    setSelectedIndex(-1);

    onUpdateGameData({
      score: newScore,
      totalAttempts: newTotalAttempts
    });
  }, [hasGuessed, guessValue, difficulty, currentCountry, gameData.score, gameData.totalAttempts, onUpdateGameData, t]);

  const handleTabPress = useCallback(() => {
    if (hasGuessed) return;
    
    if (filteredCountries.length > 0 && showAutocomplete) {
      const firstCountry = filteredCountries[0];
      const selectedValue = firstCountry.name;
      
      setGuessValue(selectedValue);
      setShowAutocomplete(false);
      setFilteredCountries([]);
      setSelectedIndex(-1);
      
      setTimeout(() => {
        submitGuess(selectedValue);
      }, 50);
    } else {
      submitGuess();
    }
  }, [filteredCountries, showAutocomplete, hasGuessed, guessValue, difficulty, submitGuess]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      handleTabPress();
    } else if (e.key === 'Enter' && !hasGuessed) {
      if (selectedIndex >= 0 && filteredCountries[selectedIndex]) {
        const selectedCountry = filteredCountries[selectedIndex];
        const selectedValue = selectedCountry.name;
        
        setGuessValue(selectedValue);
        setShowAutocomplete(false);
        setTimeout(() => {
          submitGuess(selectedValue);
        }, 50);
      } else {
        submitGuess();
      }
    } else if (e.key === 'ArrowDown' && showAutocomplete) {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < filteredCountries.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp' && showAutocomplete) {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev > 0 ? prev - 1 : filteredCountries.length - 1
      );
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }
  }, [hasGuessed, selectedIndex, filteredCountries, showAutocomplete, handleTabPress, submitGuess]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Tab' && hasGuessed && document.activeElement !== inputRef.current) {
        e.preventDefault();
        startNewRound();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [hasGuessed, startNewRound]);

  const countriesGuessed = 47 - gameData.remainingCountries.length;
  const progress = Math.round((countriesGuessed / 47) * 100);

  return (
    <div id="gameArea">
      <ScoreDisplay 
        score={gameData.score}
        totalAttempts={gameData.totalAttempts}
        countriesGuessed={countriesGuessed}
        timer={<Timer startTime={startTimeRef.current} />}
        badge={
          <span className={`difficulty-badge ${difficulty}`}>
            {t('plateMode')} - {t(difficulty)}
          </span>
        }
      />
      
      <ProgressBar progress={progress} />

      {currentCountry && (
        <>
          <LicensePlateImage
            key={`${currentCountry.code}-${currentImageNumber}`}
            countryCode={currentCountry.code}
            imageNumber={currentImageNumber}
            difficulty={difficulty}
            showOriginal={hasGuessed && difficulty === 'hard'}
          />

          <div className="input-container">
            <input
              ref={inputRef}
              type="text"
              value={guessValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={difficulty === 'easy' 
                ? t('enterCountryName')
                : t('enterCountryOrCode')
              }
              disabled={hasGuessed}
              className="guess-input"
              id="guessInput"
            />
            
            {showAutocomplete && filteredCountries.length > 0 && !hasGuessed && (
              <div className="autocomplete-list" id="autocompleteList">
                {filteredCountries.map((country, index) => (
                  <div
                    key={country.code}
                    className={`autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => {
                      const selectedValue = country.name;
                      setGuessValue(selectedValue);
                      setShowAutocomplete(false);
                      setFilteredCountries([]);
                      setSelectedIndex(-1);
                      setTimeout(() => {
                        submitGuess(selectedValue);
                      }, 50);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    {difficulty === 'easy' ? country.name : `${country.name} (${country.code})`}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="button-container">
            {!hasGuessed ? (
              <button id="submitBtn" onClick={() => submitGuess()} disabled={!guessValue || !guessValue.trim()}>
                {t('submitGuess')}
              </button>
            ) : (
              <button id="nextBtn" onClick={startNewRound}>
                {gameData.remainingCountries.length === 0 ? t('viewResults') : t('nextCountry')}
              </button>
            )}
          </div>

          {feedback && (
            <div className={`feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
              {feedback.message}
            </div>
          )}

          {hasGuessed && (
            <div className="country-info">
              {t('correctAnswer')}: <strong>{countries.find(c => c.code === currentCountry.code)?.name || currentCountry.name} ({currentCountry.code})</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlateGame;